/**
 * Textos de la interfaz y de los mensajes de la API en español e inglés (#16).
 *
 * `en` se declara con las mismas claves que `es`: si falta una traducción, no compila (y un test
 * lo comprueba también en tiempo de ejecución). Los valores admiten parámetros `{nombre}`.
 */

export type Lang = "es" | "en";
export const LANGS: Lang[] = ["es", "en"];

const es = {
  // ---- página
  "page.title": "quedicelaciencia — qué dice la ciencia",
  "page.sub": "Busca papers sobre una pregunta y clasifica qué responde cada uno a partir de su abstract.",
  "page.lang_switch": "English",
  "notice.fake":
    "Modo <b>fake</b>: los papers son reales (Europe PMC) pero las clasificaciones son aleatorias. Configura <code>PAPERJEV_BACKEND=jev</code> y <code>TYPESAFE_API_KEY</code> para usar Jev.",
  "form.question": "Pregunta",
  "form.question_ph": "Pregunta lo que quieras saber…",
  "form.query": "Términos de búsqueda en inglés",
  "form.query_help": '(opcional; si lo dejas vacío se generan a partir de la pregunta, aunque esté en español. Admite AND / OR / "frases")',
  "form.query_ph": 'Ej.: (milk OR dairy) AND ("bone mineral density" OR fracture)',
  "form.limit": "Nº de papers",
  "form.title_abs": "Buscar solo en título y abstract",
  "form.submit": "Analizar",
  "form.stop": "Parar",
  "form.busy": "Buscando y clasificando…",
  "progress.planning": "Preparando la búsqueda…",
  "progress.searching": "Buscando papers…",
  "progress.classifying": "Clasificando {classified} de {found} papers…",
  "progress.exhaustive": "Búsqueda exhaustiva: {classified} papers analizados de {found} reunidos…",
  "more.done": "Búsqueda exhaustiva completa: se han analizado los {n} papers con abstract que encuentra la consulta.",
  "more.max": "Se han analizado {n} papers, el máximo de la búsqueda exhaustiva.",
  "more.continue": "Continuar con búsqueda exhaustiva",
  "more.next": "Analizará hasta {next} papers más de los {total} que encuentra la consulta.",
  "more.cached_done": "Se analizaron {n} papers en la búsqueda guardada.",
  "more.refresh": "Actualizar",
  "more.refresh_help": "Resultado guardado: «Actualizar» repite la búsqueda desde cero.",
  // ---- veredicto
  "verdict.kicker": "Veredicto",
  "verdict.certainty": "Certeza",
  "verdict.how": "¿Cómo se calcula?",
  "headline.yes": "Sí",
  "headline.likely_yes": "Probablemente sí, con matices",
  "headline.mixed": "La evidencia está dividida",
  "headline.no_evidence": "No hay pruebas sólidas: la mayoría de estudios no son concluyentes",
  "headline.no_effect": "Probablemente no: la mayoría no encuentra efecto",
  "headline.likely_no": "Probablemente no",
  "headline.no": "No",
  "headline.insufficient": "Evidencia insuficiente",
  "explanation.main":
    "{n} papers responden a la pregunta ({strong} son revisiones sistemáticas o ensayos aleatorizados). Ponderando por calidad: {support} a favor, {against} en contra o sin efecto, {neutral} no concluyente.",
  "explanation.insufficient": "Solo {n} de los papers analizados responden a la pregunta. Prueba con otros términos de búsqueda o con más papers.",
  "certainty.alta": "alta",
  "certainty.moderada": "moderada",
  "certainty.baja": "baja",
  "certainty.muy baja": "muy baja",
  "how.weights":
    "Cada paper que responde a la pregunta pesa según su diseño (meta-análisis 1 · ensayo aleatorizado 0,8 · experimento u observación en ciencias físicas 0,7 · cuasi-experimento 0,6 · cohorte 0,55 · casos-controles 0,4 · observacional 0,35 · transversal 0,3 · modelización 0,2 · revisión narrativa o cualitativo 0,15 · animal/in vitro 0,1), multiplicado por su relevancia y por la confianza de la clasificación. En preguntas de salud, los estudios no hechos en humanos pesan un 30 %.",
  "how.headline":
    "El titular depende de qué lado reúne más peso: a favor (sí, parcial) o en contra (no, sin efecto). Si ninguno supera claramente al otro, la evidencia está dividida; si la mayoría no concluye nada, no hay pruebas sólidas. Puntuación media: {score} (sí +1 · parcial +0,5 · no concluyente 0 · sin efecto −0,5 · no −1).",
  "how.corrections":
    "Correcciones de fiabilidad: los papers retractados no cuentan; si 3 o más papers comparten al menos 2 autores, no son evidencia independiente y cada uno pesa 1/√n; si los estudios están divididos y hay 10 o más revisiones sistemáticas, el titular sigue a las revisiones.",
  "how.certainty":
    "La certeza depende de cuánta evidencia hay, de lo de acuerdo que está y de si la respaldan revisiones sistemáticas o ensayos aleatorizados (o muchos estudios observacionales consistentes).",
  "how.disclaimer": "Es un resumen automático de abstracts, no una revisión sistemática ni consejo médico.",
  "corr.title": "Correcciones de fiabilidad",
  "corr.retracted_one": "Un paper retractado no cuenta en el veredicto.",
  "corr.retracted_many": "{n} papers retractados no cuentan en el veredicto.",
  "corr.author_group":
    "{n} papers del mismo grupo de autores ({lead} y otros) no son evidencia independiente: cada uno pesa {pct} % (en conjunto, como {eq} papers).",
  "corr.groups": "{n} papers de {groups} grupos de autores con varios papers pesan menos (no son evidencia independiente)",
  "corr.groups_one": "{n} papers de 1 grupo de autores con varios papers pesan menos (no son evidencia independiente)",
  "corr.tiebreak":
    "Los estudios están divididos, así que el titular sigue a las {n} revisiones sistemáticas ({support} a favor, {against} en contra o sin efecto, {neutral} no concluyente).",
  "reviews.title": "Revisiones sistemáticas y meta-análisis",
  "reviews.none": "Ninguna entre los papers analizados. El veredicto se apoya en estudios individuales.",
  // ---- resultados
  "meta.analyzed": "{n} papers analizados de {total} encontrados",
  "meta.query": "consulta",
  "meta.errors": "{n} con error",
  "results.download": "Descargar JSON",
  "th.answer": "Respuesta",
  "th.paper": "Paper",
  "th.design": "Diseño",
  "th.relevance": "Relevancia",
  "th.confidence": "Confianza",
  "th.weight": "Peso",
  "th.weight_title": "Peso en el veredicto: diseño × humanos × relevancia × confianza",
  "row.error": "Error",
  "row.retracted": "retractado",
  "row.retracted_title": "Retractado: no cuenta en el veredicto",
  "row.group": "×{f} grupo",
  "row.group_title": "Mismo grupo de autores que otros papers: su peso se multiplica por {f}",
  "row.free": "Leer gratis",
  "row.abstract": "Abstract",
  "row.excerpt_note": "Extracto del abstract",
  "row.read_source": "abstract completo en la fuente",
  "row.model_said": " (el modelo dijo: {answer})",
  "row.uncertain": "Clasificación incierta",
  "row.non_human": " · no humanos",
  "answer.yes": "Sí",
  "answer.no": "No",
  "answer.partial": "Parcial / condicional",
  "answer.no_effect": "Sin efecto significativo",
  "answer.inconclusive": "No concluyente",
  "answer.not_addressed": "No responde",
  "design.meta_analysis": "Meta-análisis / revisión sistemática",
  "design.rct": "Ensayo controlado aleatorizado",
  "design.quasi_experimental": "Cuasi-experimento / experimento natural",
  "design.cohort": "Cohorte",
  "design.case_control": "Casos y controles",
  "design.observational": "Observacional (otros)",
  "design.cross_sectional": "Transversal",
  "design.modelling": "Modelización / simulación",
  "design.measurement": "Experimento u observación (ciencias físicas)",
  "design.animal_in_vitro": "Animal / in vitro",
  "design.narrative_review": "Revisión narrativa / opinión",
  "design.qualitative": "Cualitativo",
  "design.other": "Otro",
  // ---- avisos de la búsqueda (navegador)
  "n.source_failed": "No se pudo consultar {source}: {error}",
  "n.few_results": "Pocos resultados con «{query}»; completado con: {rest}",
  "n.relaxed": "Sin resultados con «{query}»; se ha relajado la búsqueda.",
  "n.cached": "Resultado guardado del {day} ({n} papers). Pulsa «Actualizar» para repetir la búsqueda.",
  "n.cached_failed": "No se pudo cargar la búsqueda guardada; se repite la búsqueda.",
  "n.no_papers":
    "No se han encontrado papers con abstract. Prueba con otros términos de búsqueda (en inglés) o desmarca «Buscar solo en título y abstract».",
  "n.expanded": "Búsqueda ampliada con temas de los papers relevantes: {topics}",
  "n.refs_failed": "No se pudieron seguir las referencias de las revisiones ({error})",
  "n.topics_failed": "No se pudo ampliar la búsqueda con temas relacionados ({error})",
  "n.exhaustive_source_failed": "Búsqueda exhaustiva: no se pudo completar {source} ({error})",
  "n.exhaustive_limit": "Búsqueda exhaustiva limitada a {max} papers (los más citados) de {total} encontrados.",
  // ---- mensajes de la API
  "api.access": "Esta versión de prueba requiere una clave de acceso.",
  "api.robot": "No se ha podido comprobar que no eres un robot. Recarga la página e inténtalo de nuevo.",
  "api.no_terms": "No se han encontrado términos de búsqueda en la pregunta. Escríbelos en el campo «Términos de búsqueda».",
  "api.not_found": "Búsqueda no encontrada",
  "api.budget_month":
    "Se ha alcanzado el límite de uso de este mes. Puedes consultar las búsquedas ya hechas; las nuevas volverán a estar disponibles el mes que viene.",
  "api.budget_day": "Se ha alcanzado el límite de uso de hoy. Puedes consultar las búsquedas ya hechas; vuelve mañana para hacer búsquedas nuevas.",
  "api.budget_exhaustive": "Queda poco margen de uso hoy: la búsqueda exhaustiva no está disponible hasta mañana (la rápida sí).",
  "api.limit_search":
    "Has hecho muchas búsquedas nuevas en la última hora (máximo {max}). Espera un poco; las búsquedas ya guardadas siguen disponibles.",
  "api.limit_papers": "Has analizado muchos papers en la última hora (máximo {max}). Espera un poco antes de seguir.",
  "api.limit_feedback": "Has enviado muchos reportes en la última hora (máximo {max}). Gracias; espera un poco.",
  "fb.open": "¿Mal clasificado?",
  "fb.question": "¿Qué debería decir?",
  "fb.not_relevant": "No responde a la pregunta",
  "fb.comment": "Comentario (opcional)",
  "fb.send": "Enviar",
  "fb.cancel": "Cancelar",
  "fb.thanks": "Gracias: lo revisaremos para mejorar las clasificaciones.",
  "api.translated": "Pregunta traducida al inglés: «{question}»",
  "api.translate_failed": "{error} Se busca con la pregunta original.",
  "api.synonyms": "Sinónimos añadidos: {list}",
  "api.query": "Consulta generada a partir de la pregunta: {query}",
  "api.openalex_anonymous": "OpenAlex: el presupuesto diario de la clave se ha agotado y se usa el acceso sin clave, más limitado.",
  // ---- página de una pregunta (#17) y compartir
  "qpage.title": "{question} — quedicelaciencia",
  "qpage.description": "{headline} (certeza {certainty}). {relevant} estudios responden a la pregunta; resumen automático de la evidencia con quedicelaciencia.",
  "qpage.analyzed": "Búsqueda {phase} del {date}: {n} papers analizados, {relevant} responden a la pregunta.",
  "qpage.phase_quick": "rápida",
  "qpage.phase_exhaustive": "exhaustiva",
  "qpage.top": "Estudios con más peso",
  "qpage.open": "Ver todos los papers y seguir la búsqueda",
  "qpage.new": "Hacer otra pregunta",
  "qpage.not_found": "Todavía no hay resultados guardados para esta pregunta.",
  "qpage.search_now": "Buscarla ahora",
  "gallery.title": "Preguntas destacadas",
  "footer.how": "Cómo funciona y limitaciones",
  "footer.privacy": "Privacidad y aviso legal",
  "disclaimer.health": "No es consejo médico: para decisiones de salud, consulta a un profesional sanitario.",
  "share.link": "Enlace para compartir",
  "share.copy": "Copiar enlace",
  "share.copied": "Enlace copiado",
  "share.native": "Compartir…",
  // ---- rediseño (#15)
  "nav.new": "Nueva pregunta",
  "nav.how": "Cómo funciona",
  "hero.title": "¿Qué dice la ciencia?",
  "hero.sub": "Pregunta y te resumimos lo que dicen los estudios científicos publicados.",
  "form.options": "Opciones",
  "papers.title": "Papers analizados",
  "footer.note": "Resume estudios científicos publicados; no es consejo médico.",
  "verdict.support": "{pct} a favor",
  "verdict.against": "{pct} en contra o sin efecto",
  "verdict.neutral": "{pct} no concluyente",
  "ex.1": "¿La leche es buena para los huesos?",
  "ex.2": "¿El café es malo para el corazón?",
  "ex.3": "¿Dormir poco engorda?",
  "ex.4": "¿Tienen masa los neutrinos?",
};

export type I18nKey = keyof typeof es;

const en: Record<I18nKey, string> = {
  "page.title": "quedicelaciencia — what science says",
  "page.sub": "Searches papers about a question and classifies what each one answers, based on its abstract.",
  "page.lang_switch": "Español",
  "notice.fake":
    "<b>Fake</b> mode: the papers are real (Europe PMC) but the classifications are random. Set <code>PAPERJEV_BACKEND=jev</code> and <code>TYPESAFE_API_KEY</code> to use Jev.",
  "form.question": "Question",
  "form.question_ph": "Ask anything you want to know…",
  "form.query": "Search terms in English",
  "form.query_help": '(optional; if empty they are generated from the question, even in Spanish. Supports AND / OR / "phrases")',
  "form.query_ph": 'E.g.: (milk OR dairy) AND ("bone mineral density" OR fracture)',
  "form.limit": "No. of papers",
  "form.title_abs": "Search only in title and abstract",
  "form.submit": "Analyse",
  "form.stop": "Stop",
  "form.busy": "Searching and classifying…",
  "progress.planning": "Preparing the search…",
  "progress.searching": "Searching papers…",
  "progress.classifying": "Classifying {classified} of {found} papers…",
  "progress.exhaustive": "Exhaustive search: {classified} papers analysed of {found} gathered…",
  "more.done": "Exhaustive search complete: all {n} papers with an abstract found by the query have been analysed.",
  "more.max": "{n} papers analysed, the maximum for the exhaustive search.",
  "more.continue": "Continue with exhaustive search",
  "more.next": "It will analyse up to {next} more papers of the {total} found by the query.",
  "more.cached_done": "{n} papers were analysed in the saved search.",
  "more.refresh": "Refresh",
  "more.refresh_help": "Saved result: “Refresh” runs the search again from scratch.",
  "verdict.kicker": "Verdict",
  "verdict.certainty": "Certainty",
  "verdict.how": "How is it calculated?",
  "headline.yes": "Yes",
  "headline.likely_yes": "Probably yes, with caveats",
  "headline.mixed": "The evidence is divided",
  "headline.no_evidence": "No solid evidence: most studies are inconclusive",
  "headline.no_effect": "Probably not: most find no effect",
  "headline.likely_no": "Probably not",
  "headline.no": "No",
  "headline.insufficient": "Insufficient evidence",
  "explanation.main":
    "{n} papers answer the question ({strong} are systematic reviews or randomized trials). Weighted by quality: {support} in favour, {against} against or no effect, {neutral} inconclusive.",
  "explanation.insufficient": "Only {n} of the analysed papers answer the question. Try other search terms or more papers.",
  "certainty.alta": "high",
  "certainty.moderada": "moderate",
  "certainty.baja": "low",
  "certainty.muy baja": "very low",
  "how.weights":
    "Each paper that answers the question is weighted by its design (meta-analysis 1 · randomized trial 0.8 · physical-science experiment or observation 0.7 · quasi-experiment 0.6 · cohort 0.55 · case-control 0.4 · observational 0.35 · cross-sectional 0.3 · modelling 0.2 · narrative review or qualitative 0.15 · animal/in vitro 0.1), multiplied by its relevance and by the confidence of the classification. In health questions, studies not done in humans weigh 30%.",
  "how.headline":
    "The headline depends on which side gathers more weight: in favour (yes, partial) or against (no, no effect). If neither clearly outweighs the other, the evidence is divided; if most studies conclude nothing, there is no solid evidence. Mean score: {score} (yes +1 · partial +0.5 · inconclusive 0 · no effect −0.5 · no −1).",
  "how.corrections":
    "Reliability corrections: retracted papers do not count; if 3 or more papers share at least 2 authors, they are not independent evidence and each weighs 1/√n; if studies are divided and there are 10 or more systematic reviews, the headline follows the reviews.",
  "how.certainty":
    "Certainty depends on how much evidence there is, how consistent it is and whether systematic reviews or randomized trials (or many consistent observational studies) back it.",
  "how.disclaimer": "This is an automatic summary of abstracts, not a systematic review or medical advice.",
  "corr.title": "Reliability corrections",
  "corr.retracted_one": "One retracted paper does not count in the verdict.",
  "corr.retracted_many": "{n} retracted papers do not count in the verdict.",
  "corr.author_group":
    "{n} papers from the same group of authors ({lead} et al.) are not independent evidence: each weighs {pct}% (together, like {eq} papers).",
  "corr.groups": "{n} papers from {groups} groups of authors with several papers weigh less (they are not independent evidence)",
  "corr.groups_one": "{n} papers from 1 group of authors with several papers weigh less (they are not independent evidence)",
  "corr.tiebreak":
    "Studies are divided, so the headline follows the {n} systematic reviews ({support} in favour, {against} against or no effect, {neutral} inconclusive).",
  "reviews.title": "Systematic reviews and meta-analyses",
  "reviews.none": "None among the analysed papers. The verdict relies on individual studies.",
  "meta.analyzed": "{n} papers analysed of {total} found",
  "meta.query": "query",
  "meta.errors": "{n} with errors",
  "results.download": "Download JSON",
  "th.answer": "Answer",
  "th.paper": "Paper",
  "th.design": "Design",
  "th.relevance": "Relevance",
  "th.confidence": "Confidence",
  "th.weight": "Weight",
  "th.weight_title": "Weight in the verdict: design × humans × relevance × confidence",
  "row.error": "Error",
  "row.retracted": "retracted",
  "row.retracted_title": "Retracted: does not count in the verdict",
  "row.group": "×{f} group",
  "row.group_title": "Same group of authors as other papers: its weight is multiplied by {f}",
  "row.free": "Read for free",
  "row.abstract": "Abstract",
  "row.excerpt_note": "Abstract excerpt",
  "row.read_source": "full abstract at the source",
  "row.model_said": " (the model said: {answer})",
  "row.uncertain": "Uncertain classification",
  "row.non_human": " · not in humans",
  "answer.yes": "Yes",
  "answer.no": "No",
  "answer.partial": "Partial / conditional",
  "answer.no_effect": "No significant effect",
  "answer.inconclusive": "Inconclusive",
  "answer.not_addressed": "Does not answer",
  "design.meta_analysis": "Meta-analysis / systematic review",
  "design.rct": "Randomized controlled trial",
  "design.quasi_experimental": "Quasi-experiment / natural experiment",
  "design.cohort": "Cohort",
  "design.case_control": "Case-control",
  "design.observational": "Observational (other)",
  "design.cross_sectional": "Cross-sectional",
  "design.modelling": "Modelling / simulation",
  "design.measurement": "Experiment or observation (physical sciences)",
  "design.animal_in_vitro": "Animal / in vitro",
  "design.narrative_review": "Narrative review / opinion",
  "design.qualitative": "Qualitative",
  "design.other": "Other",
  "n.source_failed": "Could not query {source}: {error}",
  "n.few_results": "Few results with “{query}”; completed with: {rest}",
  "n.relaxed": "No results with “{query}”; the search has been relaxed.",
  "n.cached": "Saved result from {day} ({n} papers). Press “Refresh” to run the search again.",
  "n.cached_failed": "The saved search could not be loaded; the search is run again.",
  "n.no_papers": "No papers with an abstract were found. Try other search terms (in English) or untick “Search only in title and abstract”.",
  "n.expanded": "Search expanded with topics from the relevant papers: {topics}",
  "n.refs_failed": "Could not follow the references of the reviews ({error})",
  "n.topics_failed": "Could not expand the search with related topics ({error})",
  "n.exhaustive_source_failed": "Exhaustive search: could not complete {source} ({error})",
  "n.exhaustive_limit": "Exhaustive search limited to {max} papers (the most cited) of {total} found.",
  "api.access": "This test version requires an access key.",
  "api.robot": "We could not verify that you are not a robot. Reload the page and try again.",
  "api.no_terms": "No search terms were found in the question. Write them in the “Search terms” field.",
  "api.not_found": "Search not found",
  "api.budget_month": "This month's usage limit has been reached. You can browse existing searches; new ones will be available again next month.",
  "api.budget_day": "Today's usage limit has been reached. You can browse existing searches; come back tomorrow for new ones.",
  "api.budget_exhaustive": "Little usage left today: the exhaustive search is unavailable until tomorrow (the quick one still works).",
  "api.limit_search": "You have run many new searches in the last hour (maximum {max}). Please wait a little; saved searches are still available.",
  "api.limit_papers": "You have analysed many papers in the last hour (maximum {max}). Please wait a little before continuing.",
  "api.limit_feedback": "You have sent many reports in the last hour (maximum {max}). Thank you; please wait a little.",
  "fb.open": "Wrong classification?",
  "fb.question": "What should it say?",
  "fb.not_relevant": "It does not answer the question",
  "fb.comment": "Comment (optional)",
  "fb.send": "Send",
  "fb.cancel": "Cancel",
  "fb.thanks": "Thank you: we will review it to improve the classifications.",
  "api.translated": "Question translated into English: “{question}”",
  "api.translate_failed": "{error} Searching with the original question.",
  "api.synonyms": "Synonyms added: {list}",
  "api.query": "Query generated from the question: {query}",
  "api.openalex_anonymous": "OpenAlex: the key's daily budget has run out and keyless access, which is more limited, is being used.",
  "qpage.title": "{question} — quedicelaciencia",
  "qpage.description": "{headline} ({certainty} certainty). {relevant} studies answer the question; automatic evidence summary by quedicelaciencia.",
  "qpage.analyzed": "{phase} search of {date}: {n} papers analysed, {relevant} answer the question.",
  "qpage.phase_quick": "Quick",
  "qpage.phase_exhaustive": "Exhaustive",
  "qpage.top": "Studies with the most weight",
  "qpage.open": "See all papers and continue the search",
  "qpage.new": "Ask another question",
  "qpage.not_found": "There are no saved results for this question yet.",
  "qpage.search_now": "Search it now",
  "gallery.title": "Featured questions",
  "footer.how": "How it works and limitations",
  "footer.privacy": "Privacy and legal notice",
  "disclaimer.health": "This is not medical advice: for health decisions, consult a healthcare professional.",
  "share.link": "Link to share",
  "share.copy": "Copy link",
  "share.copied": "Link copied",
  "share.native": "Share…",
  // ---- rediseño (#15)
  "nav.new": "New question",
  "nav.how": "How it works",
  "hero.title": "What does science say?",
  "hero.sub": "Ask a question and we summarize what published scientific studies say.",
  "form.options": "Options",
  "papers.title": "Papers analysed",
  "footer.note": "Summarizes published scientific studies; not medical advice.",
  "verdict.support": "{pct} for",
  "verdict.against": "{pct} against or no effect",
  "verdict.neutral": "{pct} inconclusive",
  "ex.1": "Is milk good for your bones?",
  "ex.2": "Is coffee bad for your heart?",
  "ex.3": "Does sleeping little make you gain weight?",
  "ex.4": "Do neutrinos have mass?",
};

export const DICTIONARIES: Record<Lang, Record<I18nKey, string>> = { es, en };

/** Texto en el idioma pedido, con los `{parámetros}` sustituidos. */
export function t(lang: Lang, key: I18nKey, params: Record<string, string | number> = {}): string {
  const text = DICTIONARIES[lang][key] ?? DICTIONARIES.es[key];
  return text.replace(/\{(\w+)\}/g, (m, name: string) => (name in params ? String(params[name]) : m));
}

/** Idioma de una etiqueta (`es-ES`, `en;q=0.8`…): español o, si no, inglés. */
export function toLang(value: string | null | undefined): Lang {
  return /^\s*es\b/i.test(value ?? "") ? "es" : value ? "en" : "es";
}

/** Número con los separadores del idioma. */
export const num = (lang: Lang, n: number) => n.toLocaleString(lang);
/** Porcentaje entero. */
export const pctText = (x: number) => `${Math.round(x * 100)}%`;
